/**
 * StageRulesSettings — Settings > Stage Rules (admin). Josh 10/12.
 *
 * Scenarios that move a deal's status automatically on every money post:
 *   "a deal in [Funded] with 3 consecutive bounces → Payment Issues"
 *   "a deal in [Payment Issues] with 3 consecutive cleared payments → Funded"
 * Scenarios run in order; the first that fires wins. Targets are Funded / Payment
 * Issues only (Default stays a deliberate Mark Default). "Preview" is read-only: it
 * lists the live deals that would move right now under the scenarios as typed.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Trash2, ArrowUp, ArrowDown, Save, RotateCcw, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { tokenStorage } from '../../lib/tokenStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${tokenStorage.getToken()}` } });
const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));

const StageRulesSettings = () => {
  const [enabled, setEnabled] = useState(true);
  const [scenarios, setScenarios] = useState([]);
  const [options, setOptions] = useState({ from: [], to: [], outcomes: [] });
  const [defaults, setDefaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/settings/stage-rules`, authHeaders())
      .then((r) => {
        setEnabled(r.data?.enabled !== false);
        setScenarios(Array.isArray(r.data?.scenarios) ? r.data.scenarios : []);
        setOptions(r.data?.options || { from: [], to: [], outcomes: [] });
        setDefaults(r.data?.defaults || []);
      })
      .catch(() => toast.error('Could not load stage rules'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = (i, patch) => setScenarios((s) => s.map((sc, idx) => (idx === i ? { ...sc, ...patch } : sc)));
  const toggleFrom = (i, st) => setScenarios((s) => s.map((sc, idx) => {
    if (idx !== i) return sc;
    const has = (sc.from || []).includes(st);
    return { ...sc, from: has ? sc.from.filter((x) => x !== st) : [...(sc.from || []), st] };
  }));
  const move = (i, dir) => setScenarios((s) => {
    const j = i + dir; if (j < 0 || j >= s.length) return s;
    const c = [...s]; [c[i], c[j]] = [c[j], c[i]]; return c;
  });
  const remove = (i) => setScenarios((s) => s.filter((_, idx) => idx !== i));
  const add = () => setScenarios((s) => [...s, { id: `scenario_${Date.now()}`, name: '', enabled: true, from: ['Funded'], outcome: 'bounce', count: 3, to: 'Payment Issues' }]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/settings/stage-rules`, { enabled, scenarios }, authHeaders());
      setScenarios(r.data?.scenarios || scenarios);
      toast.success('Stage rules saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save stage rules');
    } finally { setSaving(false); }
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const r = await axios.post(`${API}/settings/stage-rules/preview`, { scenarios }, authHeaders());
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally { setPreviewing(false); }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle>Stage change rules</CardTitle>
              <CardDescription className="mt-1">
                Applied automatically after every payment or bounce is posted (Daily Import, manual payment, Bounce button).
                Scenarios run top to bottom; the first that matches wins. A single cleared payment resets a bounce streak and
                vice-versa. Each move is logged on the deal and raises a notification. Default is never set by a rule.
              </CardDescription>
            </div>
            <label className="flex items-center gap-2 text-sm whitespace-nowrap">
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="stage-rules-enabled" />
              {enabled ? 'Rules on' : 'Rules off'}
            </label>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {scenarios.map((sc, i) => (
            <div key={sc.id || i} className={`rounded-lg border p-3 space-y-2 ${sc.enabled ? '' : 'opacity-60'}`} data-testid={`stage-scenario-${i}`}>
              <div className="flex items-center gap-2">
                <Switch checked={sc.enabled !== false} onCheckedChange={(v) => update(i, { enabled: v })} />
                <Input className="h-8 text-sm flex-1" value={sc.name || ''} placeholder="Scenario name (optional)" onChange={(e) => update(i, { name: e.target.value })} />
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => move(i, -1)} disabled={i === 0} title="Move up"><ArrowUp className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => move(i, 1)} disabled={i === scenarios.length - 1} title="Move down"><ArrowDown className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(i)} title="Remove"><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
                <span className="text-muted-foreground">A deal in</span>
                {options.from.map((st) => (
                  <label key={st} className="flex items-center gap-1">
                    <Checkbox checked={(sc.from || []).includes(st)} onCheckedChange={() => toggleFrom(i, st)} /> {st}
                  </label>
                ))}
                <span className="text-muted-foreground">with</span>
                <Input type="number" min={1} max={50} className="h-8 w-20" value={sc.count ?? ''} onChange={(e) => update(i, { count: e.target.value === '' ? '' : Number(e.target.value) })} />
                <span className="text-muted-foreground">consecutive</span>
                <Select value={sc.outcome} onValueChange={(v) => update(i, { outcome: v })}>
                  <SelectTrigger className="h-8 w-[180px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bounce">bounces</SelectItem>
                    <SelectItem value="clear">cleared payments</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-muted-foreground">→ move to</span>
                <Select value={sc.to} onValueChange={(v) => update(i, { to: v })}>
                  <SelectTrigger className="h-8 w-[170px]"><SelectValue /></SelectTrigger>
                  <SelectContent>{options.to.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ))}
          {scenarios.length === 0 && <p className="text-sm text-muted-foreground">No scenarios. Add one or restore the defaults.</p>}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={add}><Plus className="h-4 w-4 mr-1" />Add scenario</Button>
            <Button variant="outline" size="sm" onClick={() => setScenarios(defaults)} title="3 bounces → Payment Issues, 3 clears → Funded"><RotateCcw className="h-4 w-4 mr-1" />Restore defaults</Button>
            <Button variant="outline" size="sm" onClick={runPreview} disabled={previewing} data-testid="stage-rules-preview"><Eye className="h-4 w-4 mr-1" />{previewing ? 'Checking…' : 'Preview: who would move now'}</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="stage-rules-save"><Save className="h-4 w-4 mr-1" />{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </CardContent>
      </Card>

      {preview && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Preview</CardTitle>
            <CardDescription>
              {preview.checked} live deals checked · {preview.total_moves ?? preview.moves.length} would move under the scenarios as typed (unsaved changes included).
              Nothing is changed by this preview; saved rules apply on the next payment or bounce posted to each deal.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {preview.moves.length === 0 ? <p className="text-sm text-muted-foreground">No deal qualifies right now.</p> : (
              <Table>
                <TableHeader><TableRow className="text-xs"><TableHead>Deal</TableHead><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>Why</TableHead><TableHead className="text-right">Eff. balance</TableHead></TableRow></TableHeader>
                <TableBody>
                  {preview.moves.map((m) => (
                    <TableRow key={m.deal_uuid} className="text-sm">
                      <TableCell><Link className="text-blue-600 hover:underline" to={`/deals/${m.deal_uuid}`}>#{m.deal_id}</Link> <span className="text-muted-foreground">{m.name}</span></TableCell>
                      <TableCell><Badge variant="outline">{m.from}</Badge></TableCell>
                      <TableCell><Badge variant={m.to === 'Payment Issues' ? 'destructive' : 'secondary'}>{m.to}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{m.rule}</TableCell>
                      <TableCell className="text-right">{fmt(m.balance_with_fees)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {preview.truncated && <p className="text-xs text-muted-foreground mt-2">List truncated.</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StageRulesSettings;
